define('reconnectingSocket',[
  'module',
  'underscore'
], function (module, _) {
  'use strict';

  // A WebSocket that reconnects on its own, without hammering the server.
  //
  // - Exponential backoff with jitter (3 s, 6 s, 12 s… capped at 5 min), so a
  //   connection the server keeps refusing (expired token, gateway down) costs
  //   a dozen attempts an hour per tab, not one every 3 s — and a fleet of tabs
  //   does not reconnect in lockstep after an outage.
  // - The backoff only resets once a connection stayed up for a while: a socket
  //   that opens then gets closed straight away still counts as a failure.
  // - One socket at a time: connect() closes the previous one first, and a
  //   replaced or stopped socket never schedules a reconnect.
  // - Paused while the browser is offline, retried as soon as it is back.
  var BASE_DELAY_MS = 3000;
  var MAX_DELAY_MS = 5 * 60 * 1000;
  var STABLE_AFTER_MS = 30 * 1000;

  var ReconnectingSocket = function (options) {
    // options.url(): the URL to open, or null to stay disconnected (no user).
    // options.onMessage / options.onOpen: handlers, bound by the caller.
    this.options = options;
    this.socket = null;
    this.stopped = true;
    this.attempts = 0;
    this.openedAt = 0;
    this.timer = null;
    this.onOnline = _.bind(function () {
      if (!this.stopped && !this.isOpen()) {
        this.attempts = 0;
        this.open();
      }
    }, this);
  };

  _.extend(ReconnectingSocket.prototype, {

    connect: function () {
      this.stopped = false;
      this.attempts = 0;
      window.removeEventListener('online', this.onOnline);
      window.addEventListener('online', this.onOnline);
      this.open();
    },

    disconnect: function () {
      this.stopped = true;
      window.removeEventListener('online', this.onOnline);
      this.clearTimer();
      this.closeSocket();
    },

    isOpen: function () {
      return !!this.socket && this.socket.readyState === WebSocket.OPEN;
    },

    send: function (data) {
      if (this.isOpen()) {
        this.socket.send(data);
        return true;
      }
      return false;
    },

    open: function () {
      this.clearTimer();
      this.closeSocket();
      var url = this.options.url();
      if (!url) {
        return;
      }
      var socket;
      try {
        socket = new WebSocket(url);
      } catch (e) {
        this.scheduleReconnect();
        return;
      }
      this.socket = socket;
      this.openedAt = 0;

      socket.onopen = _.bind(function () {
        this.openedAt = Date.now();
        if (this.options.onOpen) {
          this.options.onOpen();
        }
      }, this);
      socket.onmessage = this.options.onMessage;
      socket.onerror = function () {
        // onclose follows and handles the retry.
      };
      socket.onclose = _.bind(function () {
        if (this.socket !== socket) {
          return; // replaced or closed on purpose
        }
        this.socket = null;
        if (this.openedAt && Date.now() - this.openedAt >= STABLE_AFTER_MS) {
          this.attempts = 0;
        }
        this.scheduleReconnect();
      }, this);
    },

    scheduleReconnect: function () {
      if (this.stopped || this.timer) {
        return;
      }
      if (window.navigator && window.navigator.onLine === false) {
        return; // the 'online' listener takes over
      }
      var ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * Math.pow(2, this.attempts));
      var delay = Math.round(ceiling / 2 + Math.random() * ceiling / 2);
      this.attempts++;
      this.timer = window.setTimeout(_.bind(function () {
        this.timer = null;
        if (!this.stopped) {
          this.open();
        }
      }, this), delay);
    },

    clearTimer: function () {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    },

    closeSocket: function () {
      var socket = this.socket;
      this.socket = null;
      if (socket) {
        socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
        try {
          socket.close();
        } catch (e) {
          // already closing
        }
      }
    }
  });

  module.exports = ReconnectingSocket;
});

